import { Inject, Injectable, Optional } from '@angular/core';
import { MessageResourceManager } from '../../resources/message-resource-manager';
import { MessageCodes } from '../../resources/message-codes';
import { ModalService, ShowCustomModalWithResulInterface, ShowExternalModalInterface } from '../../view-models/modal/modal.service';
import { ModalResult } from '../../view-models/modal/modal-result';
import { Information } from '../../messages/information';
import { BaseError } from '../../messages/base-error';
import { PopupViewModel } from '../../view-models/modal/popup-view-model';
import { ModalContainerComponent } from './modal-container.component';
import { ExternalModalViewModel } from '../../view-models/modal/external-modal-view-model';
import { ZoomUIStarterArgs } from '../../view-models/zoom/zoom-ui-starter-args';
import { EnvironmentConfiguration, UNPROXIED_ENVIRONMENT } from '@nts/std/environments';
import { EnvironmentHelper } from '@nts/std/environments';
import { MetaDataDescriptions } from '../../meta-data/meta-data-descriptions';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RoutingService } from '../../routing/routing.service';
import { AuthService } from '../../auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import { ExceptionPopupViewModel } from '../../view-models/modal/exception-popup-view-model';
import { BaseErrorInterface, BaseMessageInterface, ExternalModalViewModelInterface, IFrameGetter, MetaDataDescriptionsInterface, ModalContainerInterface, ModalResultInterface, ModalViewModelInterface, ShowExceptionPopUpFromBaseErrorInterface, ShowExceptionPopUpInterface, ShowMessageInterface, ShowPopUpModalInterface, ShowStandardModalInterface, ZoomOrchestratorViewModelInterface, ZoomResultInterface } from '@nts/std/interfaces';
import { ComponentType } from '@angular/cdk/overlay';
import { AuthQueryParams, MessageButton, MessageResult } from '@nts/std/types';


@Injectable()
export class ModalComponentService extends ModalService {

  constructor(
    public dialog: MatDialog,
    // private readonly bsModalService: BsModalService,
    private routingService: RoutingService,
    public override route: ActivatedRoute,
    private readonly environment: EnvironmentConfiguration,
    private readonly authService: AuthService,
    @Optional()
    @Inject(UNPROXIED_ENVIRONMENT) private readonly unproxiedEnvironment: EnvironmentConfiguration,
    @Inject(MAT_DIALOG_DEFAULT_OPTIONS) private readonly matDialogDefaultOptions: MatDialogConfig<any>
  ) {
    super();
  }

  async showStandardModalAsync(
    buttonsOrOpts: MessageButton | ShowStandardModalInterface,
    title: string = '',
    viewModel?: ModalViewModelInterface<any, void>,
    config: MatDialogConfig<any> = { disableClose: true },
    metaDataDescriptionsButtons: MetaDataDescriptions[] = [],
    customModalContainerComponent: ComponentType<ModalContainerInterface> = undefined,
  ): Promise<MessageResult> {

    // Destructuring sicuro all'inizio con type narrowing
    let finalButtons: MessageButton;
    let finalTitle = title;
    let finalViewModel = viewModel;
    let finalConfig = config;
    let finalMetaDataDescriptionsButtons = metaDataDescriptionsButtons;
    let finalCustomModalContainerComponent = customModalContainerComponent;

    if (typeof buttonsOrOpts !== 'number') {
      const opts = buttonsOrOpts;
      finalButtons = opts.buttons;
      finalTitle = opts.title ?? title;
      finalViewModel = opts.viewModel ?? viewModel;
      finalConfig = { ...config, ...opts.config };
      finalMetaDataDescriptionsButtons = opts.metaDataDescriptionsButtons ?? metaDataDescriptionsButtons;
      finalCustomModalContainerComponent = opts.customModalContainerComponent ?? customModalContainerComponent;
    } else {
      finalButtons = buttonsOrOpts;
    }

    if (this.ovm) {
      this.ovm.isModalOpen = true;
    }

    if (this.routingService.inIframe) {
      finalConfig.backdropClass = 'in-iframe';
    }

    // Merge del default options con le opzioni passate
    finalConfig = {
      ...this.matDialogDefaultOptions,
      ...finalConfig,
      panelClass: this.mergePanelClasses(this.matDialogDefaultOptions.panelClass, finalConfig.panelClass)
    };
    finalConfig = { ...this.matDialogDefaultOptions, ...finalConfig };

    if (finalViewModel.showFooter === true) {
      finalConfig.panelClass = this.mergePanelClasses(finalConfig.panelClass, 'has-footer');
    }

    if (this.routingService.inIframe) {
      finalConfig.panelClass = this.mergePanelClasses(finalConfig.panelClass, 'in-iframe');
    }

    const dialogRef = this.dialog.open(finalCustomModalContainerComponent ?? ModalContainerComponent, finalConfig);

    const promise = dialogRef.componentInstance.showStandardModalAsync(
      finalButtons,
      finalTitle,
      finalViewModel,
      finalMetaDataDescriptionsButtons
    );
    void promise.then(() => {
      if (this.ovm) {
        this.ovm.isModalOpen = false;
      }
    });
    return await promise;
  }

  async showZoomAsync(
    viewModel: ZoomOrchestratorViewModelInterface,
    maximizable = true,
    panelClass: string = 'zoom-container',
    width: string | undefined = undefined
  ): Promise<ModalResultInterface<ZoomResultInterface>> {
    viewModel.modalTitle = MessageResourceManager.Current.getMessageWithArgs(MessageCodes.Zoom) + ' -> ' + viewModel.rootDomainModelDisplayName;

    return await this.showCustomModalWithResultAsync<ZoomUIStarterArgs, ZoomResultInterface>({
      viewModel,
      showCancel: false,
      maximizable,
      maximized: true,
      config: {
        disableClose: true,
        panelClass,
        width
      },
      isCentered: true
    } as ShowCustomModalWithResulInterface<ZoomResultInterface>);
  }

  async showMessageAsync(
    titleOrOpts: string | ShowMessageInterface,
    message: string = '',
    buttons: MessageButton = MessageButton.Ok,
    metaDataDescriptionsButtons: MetaDataDescriptions[] = [],
    panelClass = 'message-modal',
    width: string | undefined = undefined,
    height: string | undefined = undefined,
    top: string | undefined = undefined,
    customModalContainerComponent: ComponentType<ModalContainerInterface> | undefined = undefined,
  ): Promise<MessageResult> {

    // Destructuring sicuro all'inizio con type narrowing
    let finalTitle: string;
    let finalMessage = message;
    let finalButtons = buttons;
    let finalMetaDataDescriptionsButtons = metaDataDescriptionsButtons;
    let finalPanelClass = panelClass;
    let finalWidth = width;
    let finalHeight = height;
    let finalTop = top;
    let finalCustomModalContainerComponent = customModalContainerComponent;

    if (typeof titleOrOpts !== 'string') {
      const opts = titleOrOpts;
      finalTitle = opts.title ?? '';
      finalMessage = opts.message ?? message;
      finalButtons = opts.buttons ?? buttons;
      finalMetaDataDescriptionsButtons = opts.metaDataDescriptionsButtons ?? metaDataDescriptionsButtons;
      finalPanelClass = opts.panelClass ?? panelClass;
      finalWidth = opts.width ?? width;
      finalHeight = opts.height ?? height;
      finalTop = opts.top ?? top;
      finalCustomModalContainerComponent = opts.customModalContainerComponent ?? customModalContainerComponent;
    } else {
      finalTitle = titleOrOpts;
    }

    const viewModel = new PopupViewModel();
    viewModel.list = [{
      message: finalMessage,
      detail: null
    }];

    return await this.showStandardModalAsync({
      buttons: finalButtons,
      title: finalTitle,
      viewModel,
      config: {
        position: {
          top: finalTop
        },
        width: finalWidth,
        height: finalHeight,
        panelClass: finalPanelClass
      },
      metaDataDescriptionsButtons: finalMetaDataDescriptionsButtons,
      customModalContainerComponent: finalCustomModalContainerComponent
    });
  }

  /**
   * Utilizzare per visualizzare popup partendo da un base error
   */
  async showExceptionPopUpFromBaseErrors<TResult = any>(
    params: ShowExceptionPopUpFromBaseErrorInterface,
  ): Promise<ModalResult<TResult>> {

    const viewModel = new ExceptionPopupViewModel();
    viewModel.modalTitle = params.title ? params.title : this.errorCaption;
    viewModel.errors = params.errors;

    return await this.showCustomModalWithResultAsync({
      viewModel,
      showCancel: false,
      config: {
        disableClose: true,
        panelClass: params.panelClass,
        width: params.width ? params.width : '90%',
      },
    });
  }

  /**
   * Utilizzare per visualizzare popup partendo da un errore
   */
  async showExceptionPopUp(
    errorOrOpts: Error | ShowExceptionPopUpInterface,
    caption = '',
    message = '',
    metaDataDescriptionsButton: MetaDataDescriptions = undefined,
    panelClass = 'exception-modal',
    width: string | undefined = undefined
  ): Promise<MessageResult> {

    // Destructuring sicuro all'inizio con type narrowing
    let finalError: Error;
    let finalCaption = caption;
    let finalMessage = message;
    let finalMetaDataDescriptionsButton = metaDataDescriptionsButton;
    let finalPanelClass = panelClass;
    let finalWidth = width;

    if (!(errorOrOpts instanceof Error)) {
      const opts = errorOrOpts;
      finalError = opts.error;
      finalCaption = opts.caption ?? caption;
      finalMessage = opts.message ?? message;
      finalMetaDataDescriptionsButton = opts.metaDataDescriptionsButton ?? metaDataDescriptionsButton;
      finalPanelClass = opts.panelClass ?? panelClass;
      finalWidth = opts.width ?? width;
    } else {
      finalError = errorOrOpts;
    }

    const viewModel = new PopupViewModel();
    viewModel.list = [{
      message: finalMessage || finalError.message,
      detail: this.getExceptionDetail(finalError)
    }];

    viewModel.caption = finalCaption || this.errorCaption;

    return await this.showStandardModalAsync({
      buttons: MessageButton.Ok,
      title: viewModel.caption,
      viewModel,
      config: {
        disableClose: true,
        panelClass: finalPanelClass,
        width: finalWidth
      },
      metaDataDescriptionsButtons: finalMetaDataDescriptionsButton ? [finalMetaDataDescriptionsButton] : []
    });
  }

  async showCustomModalWithResultAndCustomModalContainerAsync<TInputArgs, TResult>(
    viewModel: ModalViewModelInterface<TInputArgs, TResult>,
    customModalConatinerComponent: ComponentType<ModalContainerInterface>,
    showCancel = true,
    maximizable = false,
    maximized = false,
    config: MatDialogConfig<any> = {},
  ): Promise<ModalResult<TResult>> {
    if (this.ovm) {
      this.ovm.isModalOpen = true;
    }

    if (this.routingService.inIframe) {
      config.backdropClass = 'in-iframe';
    }

    // Merge del default options con le opzioni passate
    config = {
      ...this.matDialogDefaultOptions,
      ...config,
      panelClass: this.mergePanelClasses(this.matDialogDefaultOptions.panelClass, config.panelClass)
    };
    config = { ...this.matDialogDefaultOptions, ...config };

    if ((viewModel as ModalViewModelInterface<any, TResult>).showFooter === true) {
      config.panelClass = this.mergePanelClasses(config.panelClass, 'has-footer');
    }

    if (this.routingService.inIframe) {
      config.panelClass = this.mergePanelClasses(config.panelClass, 'in-iframe');
    }

    const dialogRef = this.dialog.open(customModalConatinerComponent, config);

    const promise = dialogRef.componentInstance.showCustomModalWithResultAsync<TResult>(
      viewModel as ModalViewModelInterface<any, TResult>,
      showCancel,
      maximizable,
      maximized,
      config
    );
    void promise.then(() => {
      if (this.ovm) {
        this.ovm.isModalOpen = false;
      }
    });
    return promise;
  }

  async showCustomModalWithResultAsync<TInputArgs, TResult>(
    viewModelOrOpts: ModalViewModelInterface<TInputArgs, TResult> | ShowCustomModalWithResulInterface<TResult>,
    showCancel = true,
    maximizable = false,
    maximized = false,
    config: MatDialogConfig<any> = {},
  ): Promise<ModalResult<TResult>> {

    // Destructuring sicuro all'inizio con type narrowing
    let finalViewModel: ModalViewModelInterface<TInputArgs, TResult>;
    let finalShowCancel = showCancel;
    let finalMaximizable = maximizable;
    let finalMaximized = maximized;
    let finalConfig = config;

    if ('viewModel' in viewModelOrOpts) {
      const opts = viewModelOrOpts;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      finalViewModel = opts.viewModel;
      finalShowCancel = opts.showCancel ?? showCancel;
      finalMaximizable = opts.maximizable ?? maximizable;
      finalMaximized = opts.maximized ?? maximized;
      finalConfig = { ...config, ...opts.config };
    } else {
      finalViewModel = viewModelOrOpts;
    }

    if (this.ovm) {
      this.ovm.isModalOpen = true;
    }

    if (this.routingService.inIframe) {
      finalConfig.backdropClass = 'in-iframe';
    }

    // Gestione maximized con panelClass array
    if (finalMaximized) {
      const fullscreenDialogClass = 'fullscreen-dialog';
      const currentPanelClass = finalConfig.panelClass ?? [];
      const panelClasses = Array.isArray(currentPanelClass)
        ? [...currentPanelClass, fullscreenDialogClass]
        : [currentPanelClass, fullscreenDialogClass].filter(Boolean);
      finalConfig = { ...finalConfig, panelClass: panelClasses };
    }

    // Posizionamento top dinamico
    const top = finalMaximized
      ? `${this.routingService.inIframe ? 2 : 52}px`
      : (this.routingService.inIframe ? null : '60px');
    const position = { ...(finalConfig.position ?? { top: null }), top };
    finalConfig = { ...finalConfig, position };

    // Merge default options
    finalConfig = {
      ...this.matDialogDefaultOptions,
      ...finalConfig,
      panelClass: this.mergePanelClasses(this.matDialogDefaultOptions.panelClass, finalConfig.panelClass)
    };
    finalConfig = { ...this.matDialogDefaultOptions, ...finalConfig };

    if ((finalViewModel as ModalViewModelInterface<any, TResult>).showFooter === true) {
      finalConfig.panelClass = this.mergePanelClasses(finalConfig.panelClass, 'has-footer');
    }

    if (this.routingService.inIframe) {
      finalConfig.panelClass = this.mergePanelClasses(finalConfig.panelClass, 'in-iframe');
    }

    const dialogRef = this.dialog.open(ModalContainerComponent, finalConfig);

    const promise = dialogRef.componentInstance.showCustomModalWithResultAsync<TResult>(
      finalViewModel,
      finalShowCancel,
      finalMaximizable,
      finalMaximized
    );
    void promise.then(() => {
      if (this.ovm) {
        this.ovm.isModalOpen = false;
      }
    });
    return promise;
  }

  private mergePanelClasses(defaultPanelClass: string | string[], customPanelClass: string | string[]): string[] {
    const toArray = (panelClass: string | string[] | undefined): string[] => {
      if (!panelClass) return [];
      return Array.isArray(panelClass) ? panelClass : [panelClass];
    };

    const defaultClasses = toArray(defaultPanelClass);
    const customClasses = toArray(customPanelClass);

    return Array.from(new Set([...defaultClasses, ...customClasses]));
  }

  async showExternalModal<TResult>(
    baseUrlOrOpts: string | ShowExternalModalInterface<TResult>,
    jsonIdentity: string | undefined = undefined,
    additionalQueryParams: URLSearchParams = new URLSearchParams(),
    modalTitle: string = '',
    externalReturn = true,
    supportRemoteClosingCheck = false,
    customExternalModalViewModel: ExternalModalViewModelInterface<any, TResult> | undefined = undefined,
    supportRemoteStatus = false,
    customModalContainerComponent: ComponentType<ModalContainerInterface> | undefined = undefined,
    config: MatDialogConfig<any> | undefined = undefined
  ): Promise<ModalResult<TResult>> {

    // Destructuring sicuro all'inizio con type narrowing
    let finalBaseUrl: string;
    let finalJsonIdentity = jsonIdentity;
    let finalAdditionalQueryParams = additionalQueryParams;
    let finalModalTitle = modalTitle;
    let finalExternalReturn = externalReturn;
    let finalSupportRemoteClosingCheck = supportRemoteClosingCheck;
    let finalCustomExternalModalViewModel = customExternalModalViewModel;
    let finalSupportRemoteStatus = supportRemoteStatus;
    let finalCustomModalContainerComponent = customModalContainerComponent;
    let finalConfig = config;

    if (typeof baseUrlOrOpts !== 'string') {
      const opts = baseUrlOrOpts;
      finalBaseUrl = opts.baseUrl;
      finalJsonIdentity = opts.jsonIdentity ?? jsonIdentity;
      finalAdditionalQueryParams = opts.additionalQueryParams ?? additionalQueryParams;
      finalModalTitle = opts.modalTitle ?? modalTitle;
      finalExternalReturn = opts.externalReturn ?? externalReturn;
      finalSupportRemoteClosingCheck = opts.supportRemoteClosingCheck ?? supportRemoteClosingCheck;
      finalCustomExternalModalViewModel = opts.customExternalModalViewModel ?? customExternalModalViewModel;
      finalSupportRemoteStatus = opts.supportRemoteStatus ?? supportRemoteStatus;
      finalCustomModalContainerComponent = opts.customModalContainerComponent ?? customModalContainerComponent;
      finalConfig = { ...config, ...opts.config };
    } else {
      finalBaseUrl = baseUrlOrOpts;
    }

    const tenantId = await this.authService.getTenantId();
    const enterpriseData = await this.authService.getEnterpriseData(tenantId);

    const viewModel = finalCustomExternalModalViewModel ?? new ExternalModalViewModel(this);
    viewModel.baseUrl = EnvironmentHelper.getProxiedUrl(
      finalBaseUrl, this.environment, this.unproxiedEnvironment
    );

    viewModel.jsonIdentity = finalJsonIdentity;
    viewModel.additionalQueryParams = finalAdditionalQueryParams ?? new URLSearchParams();

    if (enterpriseData?.enterpriseId != null && enterpriseData?.enterpriseId > -1) {
      viewModel.additionalQueryParams.set(AuthQueryParams.ENTERPRISE_ID_QUERY_KEY, enterpriseData.enterpriseId?.toString());
    }

    if (enterpriseData?.companyId != null && enterpriseData?.companyId > -1) {
      viewModel.additionalQueryParams.set(AuthQueryParams.COMPANY_ID_QUERY_KEY, enterpriseData.companyId?.toString());
    }

    finalConfig = {
      panelClass: 'external-modal-container',
      ...finalConfig
    };

    viewModel.modalTitle = finalModalTitle;
    viewModel.externalReturn = finalExternalReturn;
    viewModel.supportRemoteClosingCheck = finalSupportRemoteClosingCheck;
    viewModel.supportRemoteStatus = finalSupportRemoteStatus;

    if (finalCustomModalContainerComponent) {
      return await this.showCustomModalWithResultAndCustomModalContainerAsync<IFrameGetter, TResult>(
        viewModel,
        finalCustomModalContainerComponent,
        true,
        false,
        true,
        finalConfig
      );
    }

    // Se non viene passato un componente personalizzato, utilizziamo il metodo standard
    const res = this.showCustomModalWithResultAsync<IFrameGetter, TResult>({
      viewModel,
      showCancel: true,
      maximizable: false,
      maximized: true,
      config: finalConfig
    } as ShowCustomModalWithResulInterface<TResult>);

    return await res;
  }

  /**
   * Utilizzare per visualizzare popup partendo da una response
   */
  async showPopUp(
    informationsOrOpts: Array<BaseMessageInterface> | ShowPopUpModalInterface,
    errors: Array<BaseErrorInterface> = [],
    caption = '',
    metaDataDescriptionsButton: MetaDataDescriptionsInterface | undefined = undefined,
    panelClass = 'popup-modal',
    width: string | undefined = undefined
  ) {

    // Destructuring sicuro all'inizio con type narrowing
    let finalInformations: Array<BaseMessageInterface>;
    let finalErrors = errors;
    let finalCaption = caption;
    let finalMetaDataDescriptionsButton = metaDataDescriptionsButton;
    let finalPanelClass = panelClass;
    let finalWidth = width;

    if (!Array.isArray(informationsOrOpts)) {
      const opts = informationsOrOpts;
      finalInformations = opts.informations;
      finalErrors = opts.errors ?? errors;
      finalCaption = opts.caption ?? caption;
      finalMetaDataDescriptionsButton = opts.metaDataDescriptionsButton ?? metaDataDescriptionsButton;
      finalPanelClass = opts.panelClass ?? panelClass;
      finalWidth = opts.width ?? width;
    } else {
      finalInformations = informationsOrOpts;
    }

    const viewModel = new PopupViewModel();

    if (finalErrors.length > 0) {
      viewModel.caption = finalCaption || this.errorCaption;

      const errorList = finalErrors.map((e) => {
        let detail = '';
        detail = this.visitErrors(detail, e);

        let message = e.description;
        if (e.code?.length > 0) {
          message += `<br>Codice: ${e.code}`;
        }
        if (e.propertyName?.length > 0) {
          message += `<br>PropertyName: ${e.propertyName}`;
        }
        return {
          message,
          detail
        };
      });

      // TODO aggiungere warning/exception
      viewModel.list = errorList;

    } else if (finalInformations.length > 0) {
      // Non ci sono errori, solo informazioni, allora il messaggio principale è quello della prima informazione e lo stile è di tipo Information
      viewModel.caption = finalCaption || this.informationCaption;

      const infoList = finalInformations.map((i) => {
        const sb = '';
        this.visitInformations(sb, i);
        return {
          message: `Information: ${i.description}<br> Code: ${i.code}<br>PropertyName: ${i.propertyName}<br>`,
          detail: sb
        };
      });

      viewModel.list = infoList;
    }

    return await this.showStandardModalAsync({
      buttons: MessageButton.Ok,
      title: viewModel.caption,
      viewModel,
      metaDataDescriptionsButtons: finalMetaDataDescriptionsButton ? [finalMetaDataDescriptionsButton] : [],
      config: {
        panelClass: finalPanelClass,
        disableClose: true,
        width: finalWidth
      }
    });
  }

  private get informationCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Information);
  };

  private get warningCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Warning);
  }

  private get errorCaption() {
    return MessageResourceManager.Current.getMessage(MessageCodes.Error);
  }

  private visitErrors(sb: string, error: BaseError): string {
    if (error != null) {
      if (error.objectName && error.objectName?.length > 0) {
        sb += `${error.objectName}<br>`;
      }

      if (error.stackTrace && error.stackTrace?.length > 0) {
        sb += `${error.stackTrace}<br>`;
      }
      return this.visitErrors(sb, error.innerError);
    }
    return sb;
  }

  private visitInformations(sb: string, information: Information) {
    if (information != null) {
      const errorFormat = `DMName: ${information.objectName}<br>`;
      sb += errorFormat;
    }
  }

  private getExceptionDetail(exception: Error) {
    let detail = '';

    if (exception != null) {
      let sb = '';
      sb += `Message: ${exception.message} <br>`;
      sb += `Stack trace: ${exception.stack} <br>`;

      detail = sb;
    }

    return detail;
  }
}

